<div class="card">
  <div class="card-body">
    <h4 class="header-title mb-1">
      <i class="ri-robot-2-line me-2 text-primary"></i>Copilot
    </h4>
    <p class="text-muted mb-3">
      {{ 'channel.channelConfigCopilot.description' | translate }}
    </p>

    @if (loading) {
      <div class="text-center py-4">
        <span class="spinner-border text-primary" role="status"></span>
      </div>
    } @else {

      <!-- Enabled toggle -->
      <div class="d-flex align-items-center justify-content-between mb-4 p-3 border rounded">
        <div>
          <span class="fw-semibold text-dark">{{ 'channel.channelConfigCopilot.enable' | translate }}</span>
          <p class="text-muted small mb-0">
            {{ 'channel.channelConfigCopilot.enableDescription' | translate }}
          </p>
        </div>
        <mat-slide-toggle
          [(ngModel)]="activeCopilotModule"
          color="primary">
        </mat-slide-toggle>
      </div>

      <div [class.opacity-50]="!activeCopilotModule" [class.pe-none]="!activeCopilotModule">

        <!-- AI Model -->
        <div class="mb-3">
          <label class="form-label fw-medium">
            {{ 'channel.channelConfigCopilot.aiModel' | translate }}
            <small class="text-muted fw-normal ms-1">{{ 'channel.channelConfigCopilot.aiModelHint' | translate }}</small>
          </label>
          <div class="d-flex align-items-stretch gap-2">
            <div class="form-control d-flex align-items-center flex-grow-1">
              @if (copilotModel) {
                @if (selectedModelFree) {
                  <i class="ri-gift-line text-muted me-2"></i>
                } @else if (!selectedLogoFailed) {
                  <img
                    [src]="selectedProviderLogo"
                    [alt]="selectedProviderLabel"
                    class="provider-logo me-2"
                    (error)="selectedLogoFailed = true">
                } @else {
                  <i class="ri-sparkling-2-line text-muted me-2"></i>
                }
                <span class="badge bg-soft-primary text-primary me-2">{{ selectedProviderLabel }}</span>
                <span class="fw-medium text-truncate">{{ selectedModelName }}</span>
              } @else {
                <span class="text-muted">
                  {{ (loadingModels ? 'channel.channelConfigCopilot.loadingModels' : 'channel.channelConfigCopilot.noModelSelected') | translate }}
                </span>
              }
            </div>
            <button
              type="button"
              class="btn btn-soft-primary text-nowrap"
              (click)="openModelDialog()"
              [disabled]="loadingModels || !!modelsError">
              @if (loadingModels) {
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
              } @else {
                <i class="ri-list-settings-line me-1"></i>
              }
              {{ (copilotModel ? 'channel.channelConfigCopilot.change' : 'channel.channelConfigCopilot.select') | translate }}
            </button>
          </div>
          @if (modelsError) {
            <small class="text-danger d-block mt-1">
              <i class="ri-error-warning-line me-1"></i>{{ modelsError }}
            </small>
          }
          @if (activeCopilotModule && !copilotModel && !modelsError) {
            <small class="text-danger d-block mt-1">{{ 'channel.channelConfigCopilot.selectModelToEnable' | translate }}</small>
          }
        </div>

        <!-- Language -->
        <div class="mb-3">
          <label class="form-label fw-medium">{{ 'channel.channelConfigCopilot.responseLanguage' | translate }}</label>
          <select class="form-select" [(ngModel)]="copilotLanguage">
            @for (lang of LANGUAGES; track lang.value) {
              <option [value]="lang.value">{{ lang.label }}</option>
            }
          </select>
        </div>

        <!-- Minimum messages to unlock -->
        <div class="mb-3">
          <label class="form-label fw-medium">
            Mínimo de mensagens
            <small class="text-muted fw-normal ms-1">— antes de liberar o Copilot no atendimento</small>
          </label>
          <input
            type="number"
            class="form-control"
            min="0"
            step="1"
            [(ngModel)]="copilotMinMessages">
          <small class="text-muted d-block mt-1">
            O Copilot só fica disponível após o atendimento atingir esta quantidade de mensagens — evita disparos e gasto de créditos sem contexto suficiente.
          </small>
        </div>

        <!-- Training -->
        <div class="mb-3">
          <label class="form-label fw-medium">
            {{ 'channel.channelConfigCopilot.training' | translate }}
            <small class="text-muted fw-normal ms-1">{{ 'channel.channelConfigCopilot.trainingHint' | translate }}</small>
          </label>
          <textarea
            class="form-control"
            rows="5"
            [maxlength]="MAX_TRAINING"
            [placeholder]="'channel.channelConfigCopilot.trainingPlaceholder' | translate"
            [(ngModel)]="copilotTraining">
          </textarea>
          <small class="text-muted">{{ copilotTraining?.length ?? 0 }} / {{ MAX_TRAINING }}</small>
        </div>

        <!-- Playbook -->
        <div class="mb-3">
          <label class="form-label fw-medium">
            {{ 'channel.channelConfigCopilot.playbook' | translate }}
            <small class="text-muted fw-normal ms-1">{{ 'channel.channelConfigCopilot.playbookHint' | translate }}</small>
          </label>
          <textarea
            class="form-control"
            rows="8"
            [maxlength]="MAX_PLAYBOOK"
            [placeholder]="'channel.channelConfigCopilot.playbookPlaceholder' | translate"
            [(ngModel)]="copilotPlaybook">
          </textarea>
          <small class="text-muted">{{ copilotPlaybook?.length ?? 0 }} / {{ MAX_PLAYBOOK }}</small>
        </div>

        <!-- File upload -->
        <div class="mb-4">
          <label class="form-label fw-medium">
            <i class="ri-upload-2-line me-1"></i>{{ 'channel.channelConfigCopilot.importPlaybookFile' | translate }}
            <small class="text-muted fw-normal ms-1">{{ 'channel.channelConfigCopilot.importPlaybookFileHint' | translate }}</small>
          </label>
          <div class="d-flex align-items-center gap-2">
            <input
              type="file"
              class="form-control"
              accept=".pdf,.docx"
              (change)="onFileChange($event)"
              [disabled]="uploading">
            @if (uploading) {
              <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
            }
          </div>
          @if (copilotPlaybookFileUrl) {
            <div class="mt-2">
              <a [href]="copilotPlaybookFileUrl" target="_blank" class="btn btn-sm btn-soft-primary">
                <i class="ri-download-line me-1"></i>{{ 'channel.channelConfigCopilot.downloadCurrentFile' | translate }}
              </a>
            </div>
          }
          <small class="text-muted d-block mt-1">
            {{ 'channel.channelConfigCopilot.extractedTextHint' | translate }}
          </small>
        </div>

      </div>

      <!-- Save -->
      <div class="d-flex justify-content-end align-items-center">
        <button
          class="btn btn-primary"
          (click)="save()"
          [disabled]="saving || uploading || (activeCopilotModule && !copilotModel)">
          @if (saving) {
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
          }
          {{ 'channel.channelConfigCopilot.saveSettings' | translate }}
        </button>
      </div>

    }
  </div>
</div>
